import { FC } from 'react';
import { Controller } from 'react-hook-form';
import { DeleteOutlined } from '@mui/icons-material';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { Alert, Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import { BoxWell } from '@shared/components/BoxWell';
import { ContextualAnalysisPanel } from '@modules/Meetings/components/ContextualAnalysis/ContextualAnalysisPanel';


interface StoryThroughFieldArrayFormProps {
  storyThrough: Stories.StoryThrough;
  fieldPath: string;
  onDelete?: (storyId: number) => void;
}

export const StoryThroughFieldArrayForm: FC<StoryThroughFieldArrayFormProps> = (props) => {
  const { storyThrough, onDelete, fieldPath } = props;
  const dictionary = useDictionary();

  return (
    <BoxWell sx={{ p: 1.5 }}>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography variant="body2" fontWeight={500} fontSize={16}>
          {storyThrough.story.title}
        </Typography>
        <Chip
          label={storyThrough.story.type}
          variant="outlined"
          sx={{
            height: 'auto',
            fontSize: 12,
            fontWeight: 500,
          }}
        />
      </Stack>

      <Stack mt={2.5} gap={1}>
        <Typography variant="body2" fontWeight={500}>
          {dictionary.meetings.edit.momentOfReflection}
        </Typography>
        <Controller
          name={`${fieldPath}.momentOfReflection`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              variant="outlined"
              color="primary"
              fullWidth
              multiline
              minRows={1}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              placeholder={dictionary.forms.meeting.fieldMomentOfReflectionPlaceholder}
            />
          )}
        />
      </Stack>

      <Stack mt={2.5} gap={1}>
        <Typography variant="body2" fontWeight={500}>
          {dictionary.meetings.edit.meetingAdaptation}
        </Typography>
        <Alert severity="warning">Edits here won&apos;t affect the original story.</Alert>
        <Controller
          name={`${fieldPath}.meetingAdaptation`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              variant="outlined"
              color="primary"
              fullWidth
              multiline
              minRows={1}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Stack>

      <Stack mt={2.5} gap={1}>
        <Typography variant="body2" fontWeight={500}>
          {dictionary.meetings.edit.contextualAiFeedback}
        </Typography>

        <ContextualAnalysisPanel storyThrough={storyThrough} fieldPath={fieldPath}/>
      </Stack>

      <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end" mt={1}>
        {onDelete && (
          <IconButton onClick={() => onDelete(storyThrough.story.id)} color="error" size="small">
            <DeleteOutlined/>
          </IconButton>
        )}
      </Stack>
    </BoxWell>
  );
};
